/**
 * Discharge Instruction Preset API client functions
 */

import { API_BASE_URL } from '../config/api';
import {
  DischargeInstructionPreset,
  CreateDischargeInstructionPresetRequest,
  UpdateDischargeInstructionPresetRequest,
} from '../types/dischargeInstructionPreset';

const getAuthToken = (): string | null => {
  return localStorage.getItem('access_token') || localStorage.getItem('token');
};

const getAuthHeaders = () => {
  const token = getAuthToken();
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
  };
};

/**
 * Get all discharge instruction presets for a practice (standard + practice-specific)
 */
export const getPresetsForPractice = async (
  practiceId: string
): Promise<DischargeInstructionPreset[]> => {
  const response = await fetch(
    `${API_BASE_URL}/api/v1/practices/${practiceId}/discharge-presets`,
    {
      headers: getAuthHeaders(),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to fetch discharge instruction presets');
  }

  return response.json();
};

/**
 * Create a new discharge instruction preset for a practice
 */
export const createPreset = async (
  practiceId: string,
  presetData: CreateDischargeInstructionPresetRequest
): Promise<DischargeInstructionPreset> => {
  const response = await fetch(
    `${API_BASE_URL}/api/v1/practices/${practiceId}/discharge-presets`,
    {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(presetData),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to create discharge instruction preset');
  }

  return response.json();
};

/**
 * Update a discharge instruction preset
 */
export const updatePreset = async (
  presetId: string,
  presetData: UpdateDischargeInstructionPresetRequest
): Promise<DischargeInstructionPreset> => {
  const response = await fetch(
    `${API_BASE_URL}/api/v1/discharge-presets/${presetId}`,
    {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(presetData),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to update discharge instruction preset');
  }

  return response.json();
};

/**
 * Delete a discharge instruction preset
 */
export const deletePreset = async (presetId: string): Promise<void> => {
  const response = await fetch(
    `${API_BASE_URL}/api/v1/discharge-presets/${presetId}`,
    {
      method: 'DELETE',
      headers: getAuthHeaders(),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to delete discharge instruction preset');
  }
};

